import { useCallback, useMemo } from 'react';
import type { SetURLSearchParams } from 'react-router';
import { safeDecodeURIComponent } from '@/lib/utils';
import type { SortingState, OnChangeFn, PaginationState } from '@tanstack/react-table';
import type { FilterDescriptor, FilterOperator, FilterValue } from '@/lib/erp/filterTypes';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdvancedFilterRow {
  id: string;
  field: string;
  operator: FilterOperator;
  value: FilterValue;
}

export interface AdvancedTableState {
  // Pagination
  pageIndex: number;
  pageSize: number;
  setPageIndex: (index: number) => void;
  setPageSize: (size: number) => void;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;

  // Sorting
  sorting: SortingState;
  setSorting: OnChangeFn<SortingState>;
  orderBy: string | undefined;

  // Filters
  filterRows: AdvancedFilterRow[];
  setFilterRows: (rows: AdvancedFilterRow[]) => void;
  joinOperator: 'and' | 'or';
  setJoinOperator: (join: 'and' | 'or') => void;
  filterDescriptors: FilterDescriptor[];

  // Cleanup — call when switching away from advanced view
  clearUrlParams: () => void;
}

// ---------------------------------------------------------------------------
// Sort helpers
// ---------------------------------------------------------------------------

function serialiseSorting(sorting: SortingState): string | null {
  if (sorting.length === 0) return null;
  return sorting
    .map((s) => `${s.id}.${s.desc ? 'desc' : 'asc'}`)
    .join(',');
}

function parseSortSegment(segment: string): { id: string; desc: boolean } | null {
  const lastDot = segment.lastIndexOf('.');
  if (lastDot === -1) return null;
  const id = segment.slice(0, lastDot);
  const dir = segment.slice(lastDot + 1);
  if (!id || (dir !== 'asc' && dir !== 'desc')) return null;
  return { id, desc: dir === 'desc' };
}

function parseSorting(raw: string | null, fallback: SortingState): SortingState {
  if (!raw) return fallback;
  const segments = raw.split(',');
  const parsed = segments.map(parseSortSegment).filter(Boolean) as SortingState;
  return parsed.length > 0 ? parsed : fallback;
}

function toOrderBy(sorting: SortingState): string | undefined {
  if (sorting.length === 0) return undefined;
  return sorting
    .map((s) => `${s.id} ${s.desc ? 'desc' : 'asc'}`)
    .join(', ');
}

// ---------------------------------------------------------------------------
// Filter serialisation helpers
// Uses colon (:) as segment delimiter — Frappe fieldnames are [a-z0-9_.]
// Uses tilde (~) as row delimiter
// Uses comma (,) as array value delimiter
// Values are URI-encoded for special chars / Thai text
// ---------------------------------------------------------------------------

const VALID_OPERATORS = new Set<string>([
  'equals', 'notEquals', 'like', 'notLike', 'in', 'notIn',
  'greaterThan', 'lessThan', 'greaterOrEqual', 'lessOrEqual',
  'between', 'isSet', 'isNotSet', 'is', 'timespan',
]);

let filterIdCounter = 0;
export function generateFilterId(): string {
  return `f_${Date.now()}_${++filterIdCounter}`;
}

export function serializeFiltersToUrl(rows: AdvancedFilterRow[]): string | null {
  const complete = rows.filter((r) => r.field && r.operator);
  if (complete.length === 0) return null;

  return complete
    .map((r) => {
      // No value needed for isSet/isNotSet
      if (r.operator === 'isSet' || r.operator === 'isNotSet') {
        return `${r.field}:${r.operator}`;
      }
      // Array values (in, notIn, between)
      if (Array.isArray(r.value)) {
        const encoded = r.value.map((v) => encodeURIComponent(String(v))).join(',');
        return `${r.field}:${r.operator}:${encoded}`;
      }
      // Scalar value
      const encoded = r.value != null ? encodeURIComponent(String(r.value)) : '';
      return `${r.field}:${r.operator}:${encoded}`;
    })
    .join('~');
}

export function parseFiltersFromUrl(raw: string | null): AdvancedFilterRow[] {
  if (!raw) return [];

  return raw
    .split('~')
    .map((segment) => {
      const firstColon = segment.indexOf(':');
      if (firstColon === -1) return null;

      const field = segment.slice(0, firstColon);
      const rest = segment.slice(firstColon + 1);

      const secondColon = rest.indexOf(':');
      let operator: string;
      let rawValue: string | undefined;

      if (secondColon === -1) {
        operator = rest;
        rawValue = undefined;
      } else {
        operator = rest.slice(0, secondColon);
        rawValue = rest.slice(secondColon + 1);
      }

      if (!field || !VALID_OPERATORS.has(operator)) return null;

      let value: FilterValue = null;
      if (rawValue !== undefined) {
        if (operator === 'in' || operator === 'notIn' || operator === 'between') {
          value = rawValue.split(',').map((v) => safeDecodeURIComponent(v));
        } else {
          value = safeDecodeURIComponent(rawValue);
        }
      }

      return {
        id: generateFilterId(),
        field,
        operator: operator as FilterOperator,
        value,
      };
    })
    .filter(Boolean) as AdvancedFilterRow[];
}

function convertToFilterDescriptors(rows: AdvancedFilterRow[]): FilterDescriptor[] {
  return rows
    .filter((r) => {
      if (!r.field || !r.operator) return false;
      if (r.operator === 'isSet' || r.operator === 'isNotSet') return true;
      if (r.value === null || r.value === undefined || r.value === '') return false;
      if (Array.isArray(r.value) && r.value.length === 0) return false;
      return true;
    })
    .map((r) => ({
      field: r.field,
      operator: r.operator,
      value: r.value,
    }));
}

// ---------------------------------------------------------------------------
// URL param helpers — reads adv_* params from the shared searchParams
// ---------------------------------------------------------------------------

const ADV_PARAMS = ['adv_page', 'adv_per_page', 'adv_sort', 'adv_filters', 'adv_join'] as const;

// Stable fallback for defaultSort to avoid new [] reference on every render
const EMPTY_SORTING: SortingState = [];

function getAdvParam(sp: URLSearchParams, key: string): string | null {
  return sp.get(key);
}

// ---------------------------------------------------------------------------
// Hook — accepts searchParams from the parent to avoid duplicate subscriptions.
// DocTypeListPageShell already calls useSearchParams(); passing the result here
// prevents a 2nd/3rd subscription that can cause cascading re-renders.
// ---------------------------------------------------------------------------

export function useAdvancedTableState(options: {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  defaultPageSize?: number;
  defaultSort?: SortingState;
}): AdvancedTableState {
  const { searchParams, setSearchParams } = options;
  const defaultPageSize = options.defaultPageSize ?? 100;
  const defaultSort = options.defaultSort ?? EMPTY_SORTING;

  // Read raw values from URL (prefixed with adv_ to avoid collisions)
  const pageRaw = getAdvParam(searchParams, 'adv_page');
  const perPageRaw = getAdvParam(searchParams, 'adv_per_page');
  const sortRaw = getAdvParam(searchParams, 'adv_sort');
  const filtersRaw = getAdvParam(searchParams, 'adv_filters');
  const joinRaw = getAdvParam(searchParams, 'adv_join');

  const page = pageRaw != null ? parseInt(pageRaw, 10) || 0 : 0;
  const perPage = perPageRaw != null ? parseInt(perPageRaw, 10) || defaultPageSize : defaultPageSize;

  // Helper to update a single advanced param without clobbering others
  const setAdvParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value == null) {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  // Helper to update multiple advanced params at once
  const setAdvParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value == null) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  // Derived sorting state
  const sorting = useMemo(() => parseSorting(sortRaw, defaultSort), [sortRaw, defaultSort]);

  const setSorting: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      const next = typeof updaterOrValue === 'function'
        ? updaterOrValue(sorting)
        : updaterOrValue;
      setAdvParams({
        adv_sort: serialiseSorting(next),
        adv_page: null, // Reset to page 0 on sort change
      });
    },
    [sorting, setAdvParams],
  );

  const orderBy = useMemo(() => toOrderBy(sorting), [sorting]);

  // Pagination
  const pagination: PaginationState = useMemo(
    () => ({ pageIndex: page, pageSize: perPage }),
    [page, perPage],
  );

  const onPaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue) => {
      const next = typeof updaterOrValue === 'function'
        ? updaterOrValue(pagination)
        : updaterOrValue;
      const updates: Record<string, string | null> = {};
      if (next.pageIndex !== pagination.pageIndex) {
        updates.adv_page = next.pageIndex > 0 ? String(next.pageIndex) : null;
      }
      if (next.pageSize !== pagination.pageSize) {
        updates.adv_per_page = next.pageSize !== defaultPageSize ? String(next.pageSize) : null;
      }
      if (Object.keys(updates).length > 0) {
        setAdvParams(updates);
      }
    },
    [pagination, setAdvParams, defaultPageSize],
  );

  const setPageIndex = useCallback(
    (index: number) => setAdvParam('adv_page', index > 0 ? String(index) : null),
    [setAdvParam],
  );

  const setPageSize = useCallback(
    (size: number) => {
      setAdvParams({
        adv_per_page: size !== defaultPageSize ? String(size) : null,
        adv_page: null,
      });
    },
    [setAdvParams, defaultPageSize],
  );

  // Derived filter state
  const filterRows = useMemo(() => parseFiltersFromUrl(filtersRaw), [filtersRaw]);
  const joinOperator = (joinRaw === 'or' ? 'or' : 'and') as 'and' | 'or';

  const setFilterRows = useCallback(
    (rows: AdvancedFilterRow[]) => {
      setAdvParams({
        adv_filters: serializeFiltersToUrl(rows),
        adv_page: null, // Reset to page 0 on filter change
      });
    },
    [setAdvParams],
  );

  const setJoinOperator = useCallback(
    (join: 'and' | 'or') => {
      setAdvParams({
        adv_join: join === 'and' ? null : 'or',
        adv_page: null,
      });
    },
    [setAdvParams],
  );

  const filterDescriptors = useMemo(
    () => convertToFilterDescriptors(filterRows),
    [filterRows],
  );

  // Cleanup — remove all adv_ params from URL
  const clearUrlParams = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const key of ADV_PARAMS) {
        next.delete(key);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return {
    pageIndex: page,
    pageSize: perPage,
    setPageIndex,
    setPageSize,
    pagination,
    onPaginationChange,
    sorting,
    setSorting,
    orderBy,
    filterRows,
    setFilterRows,
    joinOperator,
    setJoinOperator,
    filterDescriptors,
    clearUrlParams,
  };
}
